import { PublicKey } from '@solana/web3.js';
import axios from 'axios';
import { BN, BorshAccountsCoder } from '@project-serum/anchor';
import { getAssociatedTokenAddress, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID } from '@solana/spl-token';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const BASE_URL = "https://us-central1-psyfi-api.cloudfunctions.net/";

const MAINNET_PROGRAM_KEYS = {
    PSYFI_V2: new PublicKey("PSYFiYqguvMXwpDooGdYV6mju92YEbFobbvW617VNcq"),
    PSYSTAKE: new PublicKey("pSystkitWgLkzprdAvraP8DSBiXwee715wiSXGJe8yr"),
};
const DEVNET_PROGRAM_KEYS = {
    PSYFI_V2: new PublicKey("95q3X9ADJv5hWt93oSaPqABPnP1rqfmjgrnto9v83LPK"),
    PSYSTAKE: new PublicKey("5LrZkBFgDkFiKEePeT2N9VuKfd2k8Rrad9PG6mKGbCRk"),
};
const EURO_PRIMITIVE_PROGRAM_ID = new PublicKey("FASQhaZQT53W9eT9wWnPoBFw8xzZDey9TbMmJj6jCQTs");

const deriveUnderlyingPoolKey = (underlyingMint) => PublicKey.findProgramAddress([underlyingMint.toBuffer(), Buffer.from("underlyingPool")], EURO_PRIMITIVE_PROGRAM_ID);
const deriveStablePoolKey = (stableMint) => PublicKey.findProgramAddress([stableMint.toBuffer(), Buffer.from("stablePool")], EURO_PRIMITIVE_PROGRAM_ID);
const deriveCallOptionMint = (euroMetaKey) => PublicKey.findProgramAddress([euroMetaKey.toBuffer(), Buffer.from("callOptionMint")], EURO_PRIMITIVE_PROGRAM_ID);
const deriveCallWriterMint = (euroMetaKey) => PublicKey.findProgramAddress([euroMetaKey.toBuffer(), Buffer.from("callWriterMint")], EURO_PRIMITIVE_PROGRAM_ID);
const derivePutOptionMint = (euroMetaKey) => PublicKey.findProgramAddress([euroMetaKey.toBuffer(), Buffer.from("putOptionMint")], EURO_PRIMITIVE_PROGRAM_ID);
const derivePutWriterMint = (euroMetaKey) => PublicKey.findProgramAddress([euroMetaKey.toBuffer(), Buffer.from("putWriterMint")], EURO_PRIMITIVE_PROGRAM_ID);
const derivePoolAuthority = () => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([Buffer.from("poolAuthority")], EURO_PRIMITIVE_PROGRAM_ID);
});
const deriveStakingRecord = (programId, recordOwner, stakePool) => __awaiter(void 0, void 0, void 0, function* () {
    return PublicKey.findProgramAddress([
        recordOwner.toBuffer(),
        stakePool.toBuffer(),
        Buffer.from("StakingRecord"),
    ], programId);
});
const deriveRewardRecord = (programId, rewardPool, rewardEpoch) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([
        rewardPool.toBuffer(),
        new BN(rewardEpoch).toArrayLike(Buffer, "le", 2),
        Buffer.from("RewardRecord"),
    ], programId);
});
const deriveVaultAccount = (programKey, collateralAssetMint, quoteAssetMint, uniqueSeed, strategyType) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([
        collateralAssetMint.toBuffer(),
        quoteAssetMint.toBuffer(),
        new BN(strategyType).toBuffer("le", 1),
        new BN(uniqueSeed).toBuffer("le", 2),
    ], programKey);
});
const deriveEpochHistory = (programKey, vaultAccount, epoch) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([
        vaultAccount.toBuffer(),
        Buffer.from(epoch.toString()),
        Buffer.from("EpochHistory"),
    ], programKey);
});
const deriveVaultAuthority = (programKey, vaultAccount) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([vaultAccount.toBuffer(), Buffer.from("VaultAuthority")], programKey);
});
const deriveVaultCollateralAccount = (programKey, vaultAccount) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([
        new PublicKey(vaultAccount).toBuffer(),
        Buffer.from("VaultCollateralAccount"),
    ], programKey);
});
const deriveVaultTokenMint = (programKey, vaultAccount) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([vaultAccount.toBuffer(), Buffer.from("VaultTokenMint")], programKey);
});
const deriveVaultTokenAccount = (programKey, vaultAccount) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([vaultAccount.toBuffer(), Buffer.from("VaultTokenAccount")], programKey);
});
const deriveWithdrawalCollateralAccount = (programKey, vaultAccount) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([vaultAccount.toBuffer(), Buffer.from("WithdrawalCollateralAccount")], programKey);
});
const deriveDepositReceipt = (programKey, userAuthority, vaultAccount, epoch) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([
        userAuthority.toBuffer(),
        new PublicKey(vaultAccount).toBuffer(),
        Buffer.from(epoch.toString()),
        Buffer.from("DepositReceipt"),
    ], programKey);
});
const deriveWithdrawalReceipt = (programKey, userAuthority, vaultAccount, epoch) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([
        userAuthority.toBuffer(),
        new PublicKey(vaultAccount).toBuffer(),
        Buffer.from(epoch.toString()),
        Buffer.from("WithdrawalReceipt"),
    ], programKey);
});
const deriveOpenOrdersAddress = (serumMarket, authority, serumDexProgramKey) => __awaiter(void 0, void 0, void 0, function* () {
    return yield PublicKey.findProgramAddress([
        Buffer.from("open-orders"),
        serumDexProgramKey.toBuffer(),
        serumMarket.toBuffer(),
        authority.toBuffer(),
    ], EURO_PRIMITIVE_PROGRAM_ID);
});
const deriveMarketAuthority = (serumMarketKey, serumDexProgramKey) => __awaiter(void 0, void 0, void 0, function* () {
    return PublicKey.findProgramAddress([
        Buffer.from("open-orders-init"),
        serumDexProgramKey.toBuffer(),
        serumMarketKey.toBuffer(),
    ], EURO_PRIMITIVE_PROGRAM_ID);
});
const deriveRequestQueue = (optionMintKey, priceCurrencyKey) => PublicKey.findProgramAddress([
    optionMintKey.toBuffer(),
    priceCurrencyKey.toBuffer(),
    Buffer.from("requestQueue"),
], EURO_PRIMITIVE_PROGRAM_ID);
const deriveCoinVault = (optionMintKey, priceCurrencyKey) => PublicKey.findProgramAddress([
    optionMintKey.toBuffer(),
    priceCurrencyKey.toBuffer(),
    Buffer.from("coinVault"),
], EURO_PRIMITIVE_PROGRAM_ID);
const derivePCVault = (optionMintKey, priceCurrencyKey) => PublicKey.findProgramAddress([
    optionMintKey.toBuffer(),
    priceCurrencyKey.toBuffer(),
    Buffer.from("pcVault"),
], EURO_PRIMITIVE_PROGRAM_ID);
const deriveSerumMarketAddress = (optionMintKey, priceCurrencyKey) => PublicKey.findProgramAddress([
    optionMintKey.toBuffer(),
    priceCurrencyKey.toBuffer(),
    Buffer.from("serumMarket"),
], EURO_PRIMITIVE_PROGRAM_ID);
const deriveSerumVaultOwner = (serumMarket, serumDexProgramKey) => __awaiter(void 0, void 0, void 0, function* () {
    const nonce = new BN(0);
    while (nonce.toNumber() < 255) {
        try {
            const vaultOwner = yield PublicKey.createProgramAddress([serumMarket.toBuffer(), nonce.toArrayLike(Buffer, "le", 8)], serumDexProgramKey);
            return [vaultOwner, nonce];
        }
        catch (e) {
            nonce.iaddn(1);
        }
    }
    throw new Error("Unable to find nonce");
});

var pdas = /*#__PURE__*/Object.freeze({
    __proto__: null,
    deriveUnderlyingPoolKey: deriveUnderlyingPoolKey,
    deriveStablePoolKey: deriveStablePoolKey,
    deriveCallOptionMint: deriveCallOptionMint,
    deriveCallWriterMint: deriveCallWriterMint,
    derivePutOptionMint: derivePutOptionMint,
    derivePutWriterMint: derivePutWriterMint,
    derivePoolAuthority: derivePoolAuthority,
    deriveStakingRecord: deriveStakingRecord,
    deriveRewardRecord: deriveRewardRecord,
    deriveVaultAccount: deriveVaultAccount,
    deriveEpochHistory: deriveEpochHistory,
    deriveVaultAuthority: deriveVaultAuthority,
    deriveVaultCollateralAccount: deriveVaultCollateralAccount,
    deriveVaultTokenMint: deriveVaultTokenMint,
    deriveVaultTokenAccount: deriveVaultTokenAccount,
    deriveWithdrawalCollateralAccount: deriveWithdrawalCollateralAccount,
    deriveDepositReceipt: deriveDepositReceipt,
    deriveWithdrawalReceipt: deriveWithdrawalReceipt,
    deriveOpenOrdersAddress: deriveOpenOrdersAddress,
    deriveMarketAuthority: deriveMarketAuthority,
    deriveRequestQueue: deriveRequestQueue,
    deriveCoinVault: deriveCoinVault,
    derivePCVault: derivePCVault,
    deriveSerumMarketAddress: deriveSerumMarketAddress,
    deriveSerumVaultOwner: deriveSerumVaultOwner
});

const PsyFiIdl = {
    version: "0.1.0",
    name: "psyfi_euros",
    instructions: [
        {
            name: "initializeVault",
            accounts: [
                {
                    name: "ownerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "managerAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "taskerAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "feeTokenAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "withdrawalCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "collateralAssetMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "quoteAssetMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "auctionCurrencyMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "currentEpochHistory",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "oracle",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "payer",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "rent",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "uniqueSeed",
                    type: "u16",
                },
                {
                    name: "strategyType",
                    type: "u8",
                },
                {
                    name: "vaultTokenDecimals",
                    type: "u8",
                },
                {
                    name: "seedVaultTokenPerCollateral",
                    type: {
                        defined: "ExchangeRate",
                    },
                },
                {
                    name: "endingExchangeRateDecimals",
                    type: "u8",
                },
                {
                    name: "maxDeposits",
                    type: "u64",
                },
                {
                    name: "fees",
                    type: {
                        defined: "VaultFees",
                    },
                },
                {
                    name: "oracleProviderId",
                    type: "u8",
                },
            ],
        },
        {
            name: "updateVaultParameters",
            accounts: [
                {
                    name: "ownerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "auctionCurrencyMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "oracle",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "maxDeposits",
                    type: "u64",
                },
                {
                    name: "fees",
                    type: {
                        defined: "VaultFees",
                    },
                },
                {
                    name: "oracleProviderId",
                    type: "u8",
                },
            ],
        },
        {
            name: "updateVaultAuthorities",
            accounts: [
                {
                    name: "ownerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "managerAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "taskerAuthority",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "updateFeeAccount",
            accounts: [
                {
                    name: "ownerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "feeTokenAccount",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "mintOptions",
            accounts: [
                {
                    name: "managerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroMeta",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "collateralPool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "optionMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "writerTokenMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "mintedOptionDest",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "mintedWriterTokenDest",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroPrimitiveProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "tokensToMint",
                    type: "u64",
                },
            ],
        },
        {
            name: "cancelOptionsPosition",
            accounts: [
                {
                    name: "managerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroMeta",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "collateralPool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "poolAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "optionMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "writerTokenMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "optionTokenSrc",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "writerTokenSrc",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroPrimitiveProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "closePostExpiry",
            accounts: [
                {
                    name: "vaultAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "nextEpochHistory",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "feeTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "withdrawalCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "collateralAssetMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "euroMeta",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "expirationData",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "writerTokenMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "writerTokenSrc",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "collateralPool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "poolAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "payer",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "euroPrimitiveProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "initializeDepositReceipt",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "depositReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "transferToDepositReceipt",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "depositReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "userCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "transferAmount",
                    type: "u64",
                },
            ],
        },
        {
            name: "transferFromDepositReceipt",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "depositReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "userCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "transferAmount",
                    type: "u64",
                },
            ],
        },
        {
            name: "payoutDepositReceipt",
            accounts: [
                {
                    name: "feeReceiver",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "depositReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "receivingVaultTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "initializeWithdrawalReceipt",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "withdrawalReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "transferToWithdrawalReceipt",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "withdrawalReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "userVaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "vaultTokenAmount",
                    type: "u64",
                },
            ],
        },
        {
            name: "transferFromWithdrawalReceipt",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "withdrawalReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "userVaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "vaultTokenAmount",
                    type: "u64",
                },
            ],
        },
        {
            name: "payoutWithdrawalReceipt",
            accounts: [
                {
                    name: "feeReceiver",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "withdrawalReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "receiptOwnerCollateralAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "withdrawalCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "feeTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "receiptOwner",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tempSolAccount",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "nativeMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rent",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "deposit",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "userCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "userVaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "collateralAmount",
                    type: "u64",
                },
            ],
        },
        {
            name: "withdraw",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "userCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "userVaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "feeTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenMint",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "vaultTokenAmount",
                    type: "u64",
                },
            ],
        },
        {
            name: "initializeSerumOpenOrders",
            accounts: [
                {
                    name: "payer",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "vaultAuthority",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "euroMeta",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "optionMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "openOrders",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "serumMarket",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "psyMarketAuthority",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroPrimitiveProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "dexProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rent",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "placeSerumOrder",
            accounts: [
                {
                    name: "taskerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "euroMeta",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "optionMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "optionTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "eventQueue",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "requestQueue",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "bids",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "asks",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "coinVault",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "priceCurrencyVault",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "openOrders",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "serumMarket",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroPrimitiveProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "dexProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rent",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "limitPrice",
                    type: "u64",
                },
                {
                    name: "clientOrderId",
                    type: "u64",
                },
            ],
        },
        {
            name: "settleSerumOrder",
            accounts: [
                {
                    name: "vaultAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroMeta",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "optionMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "optionTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "coinVault",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "priceCurrencyVault",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "openOrders",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "serumMarket",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "serumVaultSigner",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "referralAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stagingAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroPrimitiveProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "dexProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "cancelSerumOrder",
            accounts: [
                {
                    name: "taskerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "euroMeta",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "optionMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "eventQueue",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "bids",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "asks",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "openOrders",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "serumMarket",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "euroPrimitiveProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "dexProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "clientOrderId",
                    type: "u64",
                },
            ],
        },
        {
            name: "otcSwapBidAsset",
            accounts: [
                {
                    name: "taskerAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "collateralAssetMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "auctionCurrencyMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stagingAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "taskerPaymentAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "taskerDestinationAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "currentEpochHistory",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "oracle",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "declaredPrice",
                    type: "u64",
                },
                {
                    name: "stagingAmountToTrade",
                    type: "u64",
                },
            ],
        },
        {
            name: "transferToDepositReceiptForStaking",
            accounts: [
                {
                    name: "userAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "depositReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "userCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultCollateralAssetAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakePool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "transferAmount",
                    type: "u64",
                },
                {
                    name: "lockupPeriod",
                    type: "u8",
                },
                {
                    name: "forStaking",
                    type: "bool",
                },
            ],
        },
        {
            name: "claimRewardForStaker",
            accounts: [
                {
                    name: "depositReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakePool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rewardPool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "startRewardRecord",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "endRewardRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "allocatedTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "receivingTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "payoutDepositReceiptToStaking",
            accounts: [
                {
                    name: "feeReceiver",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "depositReceipt",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "vaultOwnershipTokenMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "epochHistory",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "receiptOwnerVaultTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
    ],
    accounts: [
        {
            name: "VaultAccount",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "ownerAuthority",
                        type: "publicKey",
                    },
                    {
                        name: "managerAuthority",
                        type: "publicKey",
                    },
                    {
                        name: "taskerAuthority",
                        type: "publicKey",
                    },
                    {
                        name: "vaultCollateralAssetAccount",
                        type: "publicKey",
                    },
                    {
                        name: "withdrawalCollateralAssetAccount",
                        type: "publicKey",
                    },
                    {
                        name: "vaultOwnershipTokenAccount",
                        type: "publicKey",
                    },
                    {
                        name: "feeTokenAccount",
                        type: "publicKey",
                    },
                    {
                        name: "collateralAssetMint",
                        type: "publicKey",
                    },
                    {
                        name: "quoteAssetMint",
                        type: "publicKey",
                    },
                    {
                        name: "vaultOwnershipTokenMint",
                        type: "publicKey",
                    },
                    {
                        name: "oracle",
                        type: "publicKey",
                    },
                    {
                        name: "seedVaultTokenPerCollateral",
                        type: {
                            defined: "ExchangeRate",
                        },
                    },
                    {
                        name: "endingExchangeRateDecimals",
                        type: "u8",
                    },
                    {
                        name: "strategyType",
                        type: "u8",
                    },
                    {
                        name: "vaultAccountBump",
                        type: "u8",
                    },
                    {
                        name: "uniqueSeed",
                        type: "u16",
                    },
                    {
                        name: "maxDeposits",
                        type: "u64",
                    },
                    {
                        name: "fees",
                        type: {
                            defined: "VaultFees",
                        },
                    },
                    {
                        name: "currentEpoch",
                        type: "u64",
                    },
                    {
                        name: "currentEpochHistory",
                        type: "publicKey",
                    },
                    {
                        name: "pendingCollateralAssetDeposits",
                        type: "u64",
                    },
                    {
                        name: "pendingWithdrawalTokens",
                        type: "u64",
                    },
                    {
                        name: "optionsCollateralHeld",
                        type: "u64",
                    },
                    {
                        name: "optionsActive",
                        type: "bool",
                    },
                    {
                        name: "auctionCurrencyMint",
                        type: "publicKey",
                    },
                    {
                        name: "oracleProviderId",
                        type: "u8",
                    },
                ],
            },
        },
        {
            name: "EpochHistory",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "vaultAccount",
                        type: "publicKey",
                    },
                    {
                        name: "epoch",
                        type: "u64",
                    },
                    {
                        name: "epochHistoryBump",
                        type: "u8",
                    },
                    {
                        name: "optionMarketMetaVec",
                        type: {
                            vec: {
                                defined: "OptionMarketMeta",
                            },
                        },
                    },
                    {
                        name: "optionSaleAmount",
                        type: "u64",
                    },
                    {
                        name: "endingCollateralPerVaultToken",
                        type: {
                            defined: "ExchangeRate",
                        },
                    },
                ],
            },
        },
        {
            name: "DepositReceipt",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "vaultAccount",
                        type: "publicKey",
                    },
                    {
                        name: "epochHistory",
                        type: "publicKey",
                    },
                    {
                        name: "receiptOwner",
                        type: "publicKey",
                    },
                    {
                        name: "depositAmount",
                        type: "u64",
                    },
                    {
                        name: "bump",
                        type: "u8",
                    },
                    {
                        name: "lockupPeriod",
                        type: "u8",
                    },
                    {
                        name: "forStaking",
                        type: "bool",
                    },
                    {
                        name: "stakingRecord",
                        type: "publicKey",
                    },
                ],
            },
        },
        {
            name: "WithdrawalReceipt",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "vaultAccount",
                        type: "publicKey",
                    },
                    {
                        name: "epochHistory",
                        type: "publicKey",
                    },
                    {
                        name: "receiptOwner",
                        type: "publicKey",
                    },
                    {
                        name: "withdrawalAmount",
                        type: "u64",
                    },
                    {
                        name: "bump",
                        type: "u8",
                    },
                ],
            },
        },
        {
            name: "AccountAllowlist",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "allowlistType",
                        type: "u8",
                    },
                    {
                        name: "enforceAllowlist",
                        type: "bool",
                    },
                    {
                        name: "managerAuthority",
                        type: "publicKey",
                    },
                    {
                        name: "vaultAccount",
                        type: "publicKey",
                    },
                    {
                        name: "accounts",
                        type: {
                            vec: "publicKey",
                        },
                    },
                ],
            },
        },
    ],
    types: [
        {
            name: "OptionMarketMeta",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "euroMeta",
                        type: "publicKey",
                    },
                    {
                        name: "optionSaleMarket",
                        type: {
                            defined: "OptionTokenSaleMarket",
                        },
                    },
                    {
                        name: "writerTokenAccount",
                        type: "publicKey",
                    },
                    {
                        name: "optionTokenAccount",
                        type: "publicKey",
                    },
                ],
            },
        },
        {
            name: "OptionTokenSaleMarket",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "isSaleEnabled",
                        type: "bool",
                    },
                    {
                        name: "collateralAssetPerOptionToken",
                        type: "u64",
                    },
                ],
            },
        },
        {
            name: "ExchangeRate",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "rate",
                        type: "u128",
                    },
                    {
                        name: "decimals",
                        type: "u8",
                    },
                ],
            },
        },
        {
            name: "VaultFees",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "managementFeeBps",
                        type: "u64",
                    },
                    {
                        name: "performanceFeeBps",
                        type: "u64",
                    },
                    {
                        name: "withdrawalFeeBps",
                        type: "u64",
                    },
                ],
            },
        },
        {
            name: "OracleProvider",
            type: {
                kind: "enum",
                variants: [
                    {
                        name: "PYTH",
                    },
                ],
            },
        },
    ],
    errors: [
        {
            code: 6000,
            name: "ReceiptAlreadyExchanged",
            msg: "This receipt has already been exchanged.",
        },
        {
            code: 6001,
            name: "LowUserBalance",
            msg: "Insufficient balance in user account for the collateral asset.",
        },
        {
            code: 6002,
            name: "LowVaultToken",
            msg: "Insufficient Vault Ownership Token",
        },
        {
            code: 6003,
            name: "MaxVaultCapacity",
            msg: "Maximum Vault Capacity has been reached",
        },
        {
            code: 6004,
            name: "DepositingNotSupported",
            msg: "Depositing when options are active is not supported.",
        },
        {
            code: 6005,
            name: "WithdrawingNotSupported",
            msg: "Withdrawing when options are active is not supported.",
        },
        {
            code: 6006,
            name: "DepositReceiptNotSupported",
            msg: "Please deposit directly into vault when options are not active.",
        },
        {
            code: 6007,
            name: "WithdrawalReceiptNotSupported",
            msg: "Please withdraw directly from vault when options are not active.",
        },
        {
            code: 6008,
            name: "SaleNotEnabled",
            msg: "The sale is currently not enabled. Please contact the administrators if you think that this is wrong.",
        },
        {
            code: 6009,
            name: "InsufficientAmountForSale",
            msg: "There is insufficient amount for sale to meet the order size.",
        },
        {
            code: 6010,
            name: "IncorrectOptionSalePrice",
            msg: "Price to pay per option token does not match the price set.",
        },
        {
            code: 6011,
            name: "MaximumPriceExceeded",
            msg: "Cost of quote asset exceeds max paying price set.",
        },
        {
            code: 6012,
            name: "MaximumSlippageExceeded",
            msg: "Maximum slippage exceeded.",
        },
        {
            code: 6013,
            name: "MaximumFeesExceeded",
            msg: "Maximum configurable fees exceeded.",
        },
        {
            code: 6014,
            name: "InvalidTokenBalance",
            msg: "Final token balance does not match expected.",
        },
        {
            code: 6015,
            name: "AccountIsNotInAllowList",
            msg: "Purchase is not allowed as account is not in the allowlist.",
        },
        {
            code: 6016,
            name: "OptionsHaveNotExpire",
            msg: "Closing market is not allowed yet as options have not expire.",
        },
        {
            code: 6017,
            name: "InsufficientAmountInDepositReceipt",
            msg: "Insufficient amount in Deposit Receipt for instruction.",
        },
        {
            code: 6018,
            name: "InsufficientAmountInWithdrawalReceipt",
            msg: "Insufficient amount in Withdrawal Receipt for instruction.",
        },
        {
            code: 6019,
            name: "OptionsNotActive",
            msg: "Option Market has not yet started.",
        },
        {
            code: 6020,
            name: "WriterAndOptionTokenNotMatch",
            msg: "Writer and Option Tokens amount do not match.",
        },
        {
            code: 6021,
            name: "OptionMarketInvalid",
            msg: "Option Market violates account constraints.",
        },
        {
            code: 6022,
            name: "OptionMarketMetaInvalidLength",
            msg: "Invalid vector length for OptionMarketMeta.",
        },
        {
            code: 6023,
            name: "IndexOutOfRange",
            msg: "Index out of range error.",
        },
        {
            code: 6024,
            name: "InsufficientCollateral",
            msg: "Insufficient collateral for options mint.",
        },
        {
            code: 6025,
            name: "PnlCalculationError",
            msg: "PNL Calculation Error.",
        },
        {
            code: 6026,
            name: "PayoutPendingEpochEnd",
            msg: "Receipt can only be paid out after the epoch has ended.",
        },
        {
            code: 6027,
            name: "EpochHistoryConstraint",
            msg: "EpochHistory constraint error.",
        },
        {
            code: 6028,
            name: "UserCollateralConstraint",
            msg: "User collateral asset account constraint error.",
        },
        {
            code: 6029,
            name: "UserVaultTokenConstraint",
            msg: "User vault token account constraint error.",
        },
        {
            code: 6030,
            name: "WithdrawalCollateralConstraint",
            msg: "Withdrawal collateral asset account constraint error.",
        },
        {
            code: 6031,
            name: "VaultCollateralConstraint",
            msg: "Vault collateral asset account constraint error.",
        },
        {
            code: 6032,
            name: "VaultTokenAccountConstraint",
            msg: "Vault's vault token account constraint error.",
        },
        {
            code: 6033,
            name: "OptionTokenConstraint",
            msg: "Option token account constraint error.",
        },
        {
            code: 6034,
            name: "WriterTokenConstraint",
            msg: "Writer token account constraint error.",
        },
        {
            code: 6035,
            name: "DepositReceiptConstraint",
            msg: "Deposit receipt constraint error.",
        },
        {
            code: 6036,
            name: "WithdrawalReceiptConstraint",
            msg: "Withdrawal receipt constraint error.",
        },
        {
            code: 6037,
            name: "FeeTokenAccountConstraint",
            msg: "Fee token account constraint error.",
        },
        {
            code: 6038,
            name: "EuroMetaConstraint",
            msg: "EuroMeta constraint error.",
        },
        {
            code: 6039,
            name: "UnsupportedStrategy",
            msg: "Unsuported Strategy.",
        },
        {
            code: 6040,
            name: "EuroProgramMismatch",
            msg: "Euro Primitive Program Id mismatch.",
        },
        {
            code: 6041,
            name: "DexMismatch",
            msg: "Serum DEX Program Id mismatch.",
        },
        {
            code: 6042,
            name: "OptionsAlreadyMinted",
            msg: "Options are already minted.",
        },
        {
            code: 6043,
            name: "AmountMustBeGreaterThanZero",
            msg: "Amount must be greater than 0.",
        },
        {
            code: 6044,
            name: "BpsLimitExceeded",
            msg: "Bps cannot exceed 10000",
        },
        {
            code: 6045,
            name: "PayoutForStaking",
            msg: "Cannot payout receipt that is set for staking.",
        },
        {
            code: 6046,
            name: "ReceiptNotForStaking",
            msg: "Cannot stake or claim reward for receipt, when receipt is not for staking.",
        },
        {
            code: 6047,
            name: "OwnerMismatch",
            msg: "Owner of staking record and receipt does not match.",
        },
        {
            code: 6048,
            name: "StakePoolMismatch",
            msg: "StakePool does not match.",
        },
        {
            code: 6049,
            name: "StakingRecordMismatch",
            msg: "StakingRecord does not match.",
        },
        {
            code: 6050,
            name: "InvalidLockupPeriod",
            msg: "Invalid lockup period selected.",
        },
        {
            code: 6051,
            name: "UnclaimedRewardPool",
            msg: "Some RewardPool is unclaimed.",
        },
        {
            code: 6052,
            name: "ClaimRewardPendingEpochEnd",
            msg: "Reward can only be claimed for DepositReceipt after epoch ends.",
        },
        {
            code: 6053,
            name: "MarketStateError",
            msg: "Error loading Serum market state.",
        },
        {
            code: 6054,
            name: "AuctionMintMismatch",
            msg: "The mint specified for the auction currency doesn't match the vault.",
        },
        {
            code: 6055,
            name: "TaskerSignerError",
            msg: "Tasker did not sign this transaction.",
        },
        {
            code: 6056,
            name: "TaskerValidationError",
            msg: "Account specified as tasker is not the authorized tasker on this vault.",
        },
        {
            code: 6057,
            name: "TaskerCollateralAccountValidationError",
            msg: "Collateral destination does not match vault's collateral account.",
        },
        {
            code: 6058,
            name: "EpochHistoryValidationError",
            msg: "Account specified as epoch history is not the epoch history on this vault.",
        },
        {
            code: 6059,
            name: "OracleConfidenceError",
            msg: "Pyth Oracle confidence too low to price accurately, try again later.",
        },
        {
            code: 6060,
            name: "OracleResponseIssue",
            msg: "Pyth Oracle responding with zero or negative price.",
        },
        {
            code: 6061,
            name: "OracleExponentIssue",
            msg: "Pyth Oracle esponding with non-negative exponent.",
        },
        {
            code: 6062,
            name: "OracleValidationError",
            msg: "Oracle doesn't match vault Oracle.",
        },
        {
            code: 6063,
            name: "TaskerMintMismatch",
            msg: "The mint used for tasker's payment doesn't match the collateral pool.",
        },
        {
            code: 6064,
            name: "TokenOwnerMismatch",
            msg: "Invalid owner of token account.",
        },
        {
            code: 6065,
            name: "TaskerNotEnoughAssets",
            msg: "Tasker is not bringing enough underlying assets, offer more assets.",
        },
        {
            code: 6066,
            name: "TaskerDeclaredPriceTooLow",
            msg: "Tasker's declared price is too low: offer a better exchange rate.",
        },
        {
            code: 6067,
            name: "TaskerDeclaredPriceTooHigh",
            msg: "Tasker's declared price is too high: check for typo.",
        },
        {
            code: 6068,
            name: "StagingAccountEmpty",
            msg: "The staging account has no assets: perhaps the auction has not ended?",
        },
        {
            code: 6069,
            name: "NumericalOverflow",
            msg: "Numerical Overflow",
        },
        {
            code: 6070,
            name: "OptionWriterMismatchError",
            msg: "Option and Writer token size mismatch.",
        },
        {
            code: 6071,
            name: "InvalidNativeMint",
            msg: "Invalid native mint provided",
        },
        {
            code: 6072,
            name: "InvalidReceiptOwner",
            msg: "Receipt owner provided does not match owner recorded",
        },
        {
            code: 6073,
            name: "OracleNotSupported",
            msg: "Instruction is not supported by oracle type.",
        },
    ],
    metadata: {
        address: "PSYFiYqguvMXwpDooGdYV6mju92YEbFobbvW617VNcq",
    },
};

const IDL = {
    version: "0.1.0",
    name: "psy_stake",
    instructions: [
        {
            name: "createStakePool",
            accounts: [
                {
                    name: "stakePoolAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "payer",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingTokenMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakingTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rent",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "uniqueSeed",
                    type: "u16",
                },
                {
                    name: "nextEpochStartTime",
                    type: "i64",
                },
                {
                    name: "epochDuration",
                    type: "i64",
                },
            ],
        },
        {
            name: "createRewardPool",
            accounts: [
                {
                    name: "stakePoolAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "payer",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "rewardPoolAuthority",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rewardPool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rewardRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rewardTokenMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rewardTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "allocatedTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rent",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "startingEpoch",
                    type: "u16",
                },
                {
                    name: "distributionType",
                    type: {
                        defined: "DistributionType",
                    },
                },
                {
                    name: "rewardPerEpoch",
                    type: "u64",
                },
                {
                    name: "epochRewardDecimals",
                    type: "u8",
                },
            ],
        },
        {
            name: "updateRewardPool",
            accounts: [
                {
                    name: "rewardPoolAuthority",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rewardPool",
                    isMut: true,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "distributionType",
                    type: {
                        defined: "DistributionType",
                    },
                },
                {
                    name: "rewardPerEpoch",
                    type: "u64",
                },
                {
                    name: "isActive",
                    type: "bool",
                },
            ],
        },
        {
            name: "createStakingRecord",
            accounts: [
                {
                    name: "recordOwner",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "stakePool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "stakeToken",
            accounts: [
                {
                    name: "recordOwner",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "ownerTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "amount",
                    type: "u64",
                },
                {
                    name: "lockupPeriod",
                    type: "u8",
                },
            ],
        },
        {
            name: "unstakeToken",
            accounts: [
                {
                    name: "recordOwner",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "ownerTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "amount",
                    type: "u64",
                },
            ],
        },
        {
            name: "claimReward",
            accounts: [
                {
                    name: "recordOwner",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "stakePool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rewardPool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "startRewardRecord",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "endRewardRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "ownerTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "allocatedTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "createRewardRecord",
            accounts: [
                {
                    name: "payer",
                    isMut: true,
                    isSigner: true,
                },
                {
                    name: "stakePool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rewardPool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "newRewardRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "prevRewardRecord",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rewardTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "allocatedTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "updateRewardRecord",
            accounts: [
                {
                    name: "stakePool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rewardPool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "curRewardRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "prevRewardRecord",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "rewardTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "allocatedTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "updateStakePoolEpoch",
            accounts: [
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "systemProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
        {
            name: "stakeByVault",
            accounts: [
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "stakingTokenMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakePool",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "ownerTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "stakingTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [
                {
                    name: "amount",
                    type: "u64",
                },
                {
                    name: "lockupPeriod",
                    type: "u8",
                },
            ],
        },
        {
            name: "claimRewardByVault",
            accounts: [
                {
                    name: "vaultAccount",
                    isMut: false,
                    isSigner: true,
                },
                {
                    name: "stakingTokenMint",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakePool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "stakingRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "rewardPool",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "startRewardRecord",
                    isMut: false,
                    isSigner: false,
                },
                {
                    name: "endRewardRecord",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "allocatedTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "ownerTokenAccount",
                    isMut: true,
                    isSigner: false,
                },
                {
                    name: "tokenProgram",
                    isMut: false,
                    isSigner: false,
                },
            ],
            args: [],
        },
    ],
    accounts: [
        {
            name: "StakePool",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "stakePoolAuthority",
                        type: "publicKey",
                    },
                    {
                        name: "stakingTokenMint",
                        type: "publicKey",
                    },
                    {
                        name: "stakingTokenAccount",
                        type: "publicKey",
                    },
                    {
                        name: "rewardPoolCount",
                        type: "u8",
                    },
                    {
                        name: "currentEpoch",
                        type: "u16",
                    },
                    {
                        name: "epochDuration",
                        type: "i64",
                    },
                    {
                        name: "nextEpochStartTime",
                        type: "i64",
                    },
                    {
                        name: "inactiveEpochVec",
                        type: {
                            vec: "u16",
                        },
                    },
                    {
                        name: "startingEpochVec",
                        type: {
                            vec: "u16",
                        },
                    },
                    {
                        name: "totalRewardUnits",
                        type: "u64",
                    },
                    {
                        name: "stakePoolBump",
                        type: "u8",
                    },
                    {
                        name: "stakingTokenAccountBump",
                        type: "u8",
                    },
                    {
                        name: "uniqueSeed",
                        type: "u16",
                    },
                ],
            },
        },
        {
            name: "RewardPool",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "stakePool",
                        type: "publicKey",
                    },
                    {
                        name: "rewardPoolAuthority",
                        type: "publicKey",
                    },
                    {
                        name: "poolId",
                        type: "u8",
                    },
                    {
                        name: "rewardTokenMint",
                        type: "publicKey",
                    },
                    {
                        name: "rewardTokenAccount",
                        type: "publicKey",
                    },
                    {
                        name: "allocatedTokenAccount",
                        type: "publicKey",
                    },
                    {
                        name: "distributionType",
                        type: {
                            defined: "DistributionType",
                        },
                    },
                    {
                        name: "constantRewardPerEpoch",
                        type: "u64",
                    },
                    {
                        name: "percentageRewardMbpsPerEpoch",
                        type: "u64",
                    },
                    {
                        name: "startingEpoch",
                        type: "u16",
                    },
                    {
                        name: "isActive",
                        type: "bool",
                    },
                    {
                        name: "epochRewardDecimals",
                        type: "u8",
                    },
                    {
                        name: "rewardPoolBump",
                        type: "u8",
                    },
                    {
                        name: "rewardTokenAccountBump",
                        type: "u8",
                    },
                    {
                        name: "allocatedTokenAccountBump",
                        type: "u8",
                    },
                ],
            },
        },
        {
            name: "StakingRecord",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "stakePool",
                        type: "publicKey",
                    },
                    {
                        name: "recordOwner",
                        type: "publicKey",
                    },
                    {
                        name: "stakedAmount",
                        type: "u64",
                    },
                    {
                        name: "rewardUnits",
                        type: "u64",
                    },
                    {
                        name: "lockUpExpiry",
                        type: "i64",
                    },
                    {
                        name: "lastEpochClaimedVec",
                        type: {
                            vec: "u16",
                        },
                    },
                    {
                        name: "recordBump",
                        type: "u8",
                    },
                ],
            },
        },
        {
            name: "RewardRecord",
            type: {
                kind: "struct",
                fields: [
                    {
                        name: "stakePool",
                        type: "publicKey",
                    },
                    {
                        name: "rewardPool",
                        type: "publicKey",
                    },
                    {
                        name: "epoch",
                        type: "u16",
                    },
                    {
                        name: "lastUpdated",
                        type: "i64",
                    },
                    {
                        name: "epochEndTimestamp",
                        type: "i64",
                    },
                    {
                        name: "cummulativeReward",
                        type: "u64",
                    },
                    {
                        name: "epochReward",
                        type: "u64",
                    },
                    {
                        name: "totalRewardAllocated",
                        type: "u64",
                    },
                    {
                        name: "recordBump",
                        type: "u8",
                    },
                    {
                        name: "isUpdatable",
                        type: "bool",
                    },
                ],
            },
        },
    ],
    types: [
        {
            name: "DistributionType",
            type: {
                kind: "enum",
                variants: [
                    {
                        name: "Constant",
                    },
                    {
                        name: "Percentage",
                    },
                ],
            },
        },
    ],
    errors: [
        {
            code: 6000,
            name: "InvalidStartTime",
            msg: "Start time of next epoch must be in the future.",
        },
        {
            code: 6001,
            name: "MinEpochDuration",
            msg: "Epoch duration must be at least 4 hours.",
        },
        {
            code: 6002,
            name: "InvalidStartingEpoch",
            msg: "Starting epoch of RewardPool cannot be in the past.",
        },
        {
            code: 6003,
            name: "InvalidPreviousRecord",
            msg: "Previous record must be from one epoch before current record.",
        },
        {
            code: 6004,
            name: "InvalidDistributionType",
            msg: "Distribution type is not supported.",
        },
        {
            code: 6005,
            name: "InvalidPercentage",
            msg: "Percentage chosen is invalid.",
        },
        {
            code: 6006,
            name: "RewardPoolsLimit",
            msg: "Max. no. of RewardPool per stake pool has been reached.",
        },
        {
            code: 6007,
            name: "RewardPoolInactive",
            msg: "RewardPool is no longer active.",
        },
        {
            code: 6008,
            name: "RewardRecordEpochConstraint",
            msg: "RewardRecord cannot be created for future epochs.",
        },
        {
            code: 6009,
            name: "LastUpdateMoreRecent",
            msg: "RewardRecord cannot be updated as last update was more recent to epoch end.",
        },
        {
            code: 6010,
            name: "EpochHasNotEnded",
            msg: "The current epoch has not ended.",
        },
        {
            code: 6011,
            name: "InsufficientAmount",
            msg: "Insufficient amount for staking or unstaking.",
        },
        {
            code: 6012,
            name: "InvalidLockupPeriod",
            msg: "Invalid lockup period selected.",
        },
        {
            code: 6013,
            name: "LockupPeriodTooShort",
            msg: "Selected lockup period is too short.",
        },
        {
            code: 6014,
            name: "LockupHasNotExpire",
            msg: "Lockup period has not expire.",
        },
        {
            code: 6015,
            name: "UnclaimedRewardPool",
            msg: "Some RewardPool is unclaimed.",
        },
        {
            code: 6016,
            name: "EndRecordMustBeSameOrNewer",
            msg: "End RewardRecord must be from the same or later epoch than start RewardRecord.",
        },
        {
            code: 6017,
            name: "NoRewardsAvailable",
            msg: "No rewards available for claiming.",
        },
        {
            code: 6018,
            name: "InvalidStartRewardRecord",
            msg: "Start RewardRecord should be from the last epoch claimed.",
        },
        {
            code: 6019,
            name: "RewardAlreadyClaimed",
            msg: "Reward has already been claimed.",
        },
        {
            code: 6020,
            name: "RewardPoolHasNotStarted",
            msg: "Cannot change a RewardPool to inactive before it starts.",
        },
        {
            code: 6021,
            name: "OwnerTokenAccountInvalid",
            msg: "Owner token account is invalid.",
        },
        {
            code: 6022,
            name: "RewardRecordCannotBeUpdated",
            msg: "RewardRecord is not updatable.",
        },
        {
            code: 6023,
            name: "InvalidSigner",
            msg: "Signer of transaction must be an authorized vault account.",
        },
        {
            code: 6024,
            name: "DecimalsLimit",
            msg: "Limit of decimals allowed has been exceeded.",
        },
    ],
    metadata: {
        address: "pSystkitWgLkzprdAvraP8DSBiXwee715wiSXGJe8yr",
    },
};

const psyFiAccountsCoder = new BorshAccountsCoder(PsyFiIdl);
const psyStakeAccountsCoder = new BorshAccountsCoder(IDL);
const fetchVaultAccount = (connection, accountKey) => __awaiter(void 0, void 0, void 0, function* () {
    const accountInfo = yield connection.getAccountInfo(accountKey, "confirmed");
    if (!accountInfo)
        return;
    return psyFiAccountsCoder.decode("VaultAccount", accountInfo.data);
});
const fetchDepositReceipt = (connection, accountKey) => __awaiter(void 0, void 0, void 0, function* () {
    const accountInfo = yield connection.getAccountInfo(accountKey, "confirmed");
    if (!accountInfo)
        return;
    return psyFiAccountsCoder.decode("DepositReceipt", accountInfo.data);
});
const fetchStakePoolAccount = (accountKey, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const result = yield connection.getAccountInfo(accountKey, "confirmed");
    if (!result)
        return;
    return psyStakeAccountsCoder.decode("StakePool", result.data);
});
const fetchStakingRecord = (accountKey, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const result = yield connection.getAccountInfo(accountKey, "confirmed");
    if (!result)
        return;
    return psyStakeAccountsCoder.decode("StakingRecord", result.data);
});
const fetchRewardPoolAccount = function (accountKey, connection) {
    return __awaiter(this, void 0, void 0, function* () {
        const result = yield connection.getAccountInfo(accountKey, "confirmed");
        if (!result)
            return;
        return psyStakeAccountsCoder.decode("RewardPool", result.data);
    });
};
const fetchRewardRecord = (accountKey, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const result = yield connection.getAccountInfo(accountKey, "confirmed");
    if (!result)
        return;
    return psyStakeAccountsCoder.decode("RewardRecord", result.data);
});
const fetchWithdrawalReceipt = (accountKey, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const result = yield connection.getAccountInfo(accountKey, "confirmed");
    if (!result)
        return;
    return psyFiAccountsCoder.decode("WithdrawalReceipt", result.data);
});

const getRewardRecordData = (psyStakeProgramKey, rewardPoolKeyString, epoch, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const rewardPoolKey = new PublicKey(rewardPoolKeyString);
    const [rewardRecord] = yield deriveRewardRecord(psyStakeProgramKey, rewardPoolKey, epoch);
    try {
        const rewardRecordData = yield fetchRewardRecord(rewardRecord, connection);
        if (rewardRecordData)
            return rewardRecordData;
    }
    catch (e) {
        return;
    }
});
const getEpochsToClaimForPool = (poolId, stakingRecord, stakePool) => {
    const poolIdx = poolId - 1;
    const { currentEpoch } = stakePool;
    // No epochs to claim if stakingRecord does not exist.
    if (!stakingRecord || !currentEpoch || !stakePool)
        return;
    // Epoch at which RewardPool becomes inactive.
    const inactiveEpoch = stakePool.inactiveEpochVec[poolIdx];
    // Epoch at which RewardPool starts.
    const startingEpoch = stakePool.startingEpochVec[poolIdx];
    // The last epoch which was claimed.
    const lastEpochClaimed = stakingRecord.lastEpochClaimedVec[poolIdx];
    // Epoch to pass to instruction as the start epoch, to be used
    // for retrieving the cummulativeReward up to this epoch.
    // This should either be the last epoch claimed or the starting epoch
    // of the RewardPool (if it is later).
    // Note that only rewards from the next epoch after this will be claimed.
    const startClaimEpoch = Math.max(lastEpochClaimed, startingEpoch);
    // Epoch to pass to instruction as the end epoch, to be used
    // for retrieving the cummulativeReward up to this epoch.
    // This should either be the last completed epoch or the epoch in which
    // RewardPool became inactive (if it is earlier).
    // Note that rewards between startClaimEpoch+1 to endClaimEpoch (inclusive)
    // will be claimed.
    const endClaimEpoch = inactiveEpoch > 0
        ? Math.min(currentEpoch - 1, inactiveEpoch)
        : currentEpoch - 1;
    const hasRewardPoolCompletedAnEpoch = startingEpoch < currentEpoch;
    if (!hasRewardPoolCompletedAnEpoch ||
        // Cannot start claiming from an epoch later than the end claim.
        startClaimEpoch > endClaimEpoch ||
        // Can only set startClaimEpoch as endClaimEpoch, if
        // it is the starting epoch of the RewardPool.
        (startClaimEpoch === endClaimEpoch && startClaimEpoch !== startingEpoch) ||
        // End claim epoch cannot already be claimed.
        lastEpochClaimed >= endClaimEpoch) {
        return;
    }
    return [startClaimEpoch, endClaimEpoch];
};
const fetchRewardClaims = (rewardTokenAddress, rewardMintAddress, poolId, epochRewardDecimals, stakingRecord, stakePool, claimableRewards, program, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const epochsToClaimForPool = getEpochsToClaimForPool(poolId, stakingRecord, stakePool);
    // Fetch RewardRecord startClaimEpoch
    if (!(epochsToClaimForPool === null || epochsToClaimForPool === void 0 ? void 0 : epochsToClaimForPool.length)) {
        return claimableRewards;
    }
    const [startClaimEpoch, endClaimEpoch] = epochsToClaimForPool;
    const startRewardRecordData = yield getRewardRecordData(program, rewardTokenAddress, startClaimEpoch, connection);
    const endRewardRecordData = yield getRewardRecordData(program, rewardTokenAddress, endClaimEpoch, connection);
    let amountPerRewardUnit = 0;
    if (startClaimEpoch === endClaimEpoch) {
        // amountPerRewardUnit == cummulative of startClaimEpoch.
        amountPerRewardUnit =
            (startRewardRecordData === null || startRewardRecordData === void 0 ? void 0 : startRewardRecordData.cummulativeReward.toNumber()) /
                Math.pow(10, epochRewardDecimals);
    }
    else {
        // @ts-ignore
        amountPerRewardUnit =
            (endRewardRecordData.cummulativeReward.toNumber() -
                startRewardRecordData.cummulativeReward.toNumber()) /
                Math.pow(10, epochRewardDecimals);
    }
    const tokenSupply = yield connection.getTokenSupply(new PublicKey(rewardMintAddress));
    const claimableAmount = Math.floor(amountPerRewardUnit * stakingRecord.rewardUnits.toNumber()) /
        Math.pow(10, tokenSupply.value.decimals);
    if (!claimableRewards[rewardMintAddress]) {
        claimableRewards[rewardMintAddress] = {
            totalClaimableAmount: claimableAmount,
            tokenSymbol: rewardMintAddress,
            epochsToClaim: {
                [poolId]: epochsToClaimForPool,
            },
        };
    }
    else {
        claimableRewards[rewardMintAddress]["totalClaimableAmount"] +=
            claimableAmount;
        claimableRewards[rewardMintAddress]["epochsToClaim"][poolId] =
            epochsToClaimForPool;
    }
    return claimableRewards;
});

// Returns duration from current time to endingTimeInUnixEpoch, in seconds, if time
// is in the future. Otherwise, returns 0.
const getRemainingTimeInSeconds = (endingTimeInUnixEpoch) => {
    const currentTimeInUnixEpoch = new Date().getTime() / 1000;
    const getRemainingTimeInSeconds = Math.trunc(endingTimeInUnixEpoch - currentTimeInUnixEpoch);
    return getRemainingTimeInSeconds > 0 ? getRemainingTimeInSeconds : 0;
};
function round(value, minimumFractionDigits, maximumFractionDigits) {
    const formattedValue = value.toLocaleString("en", {
        useGrouping: false,
        minimumFractionDigits,
        maximumFractionDigits,
    });
    return Number(formattedValue);
}

const getUserWalletPosition = (walletAddress, vaultInfo, vaultAccount, connection, stakePool = null, VaultPoolReward = null, isMainnet = true) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const psyFiProgramKey = isMainnet
        ? MAINNET_PROGRAM_KEYS.PSYFI_V2
        : DEVNET_PROGRAM_KEYS.PSYFI_V2;
    const psyStakeProgramKey = isMainnet
        ? MAINNET_PROGRAM_KEYS.PSYSTAKE
        : DEVNET_PROGRAM_KEYS.PSYSTAKE;
    const vaultAccountData = yield fetchVaultAccount(connection, vaultAccount);
    if (!vaultAccountData)
        throw Error("Vault Account does not exist.");
    const [vaultTokenMint] = yield deriveVaultTokenMint(psyFiProgramKey, vaultAccount);
    const userVaultTokenAccount = yield getAssociatedTokenAddress(vaultTokenMint, walletAddress, true, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
    let vaultTokensInWallet = 0;
    let vaultTokensStaked = 0;
    let claimableRewards = {};
    let userVaultTokenAmount;
    let rewardMultiplier = 1;
    let remainingLockupInSeconds = 0;
    try {
        userVaultTokenAmount = yield ((_a = (yield connection.getTokenAccountBalance(userVaultTokenAccount))) === null || _a === void 0 ? void 0 : _a.value);
        if (userVaultTokenAmount) {
            vaultTokensInWallet = Number(userVaultTokenAmount.amount);
        }
    }
    catch (err) {
        return;
    }
    const [depositReceiptKey] = yield deriveDepositReceipt(psyFiProgramKey, walletAddress, vaultAccount, vaultAccountData.currentEpoch.toNumber());
    const depositReceipt = yield fetchDepositReceipt(connection, depositReceiptKey);
    const [withdrawalReceiptAccount] = yield deriveWithdrawalReceipt(psyFiProgramKey, walletAddress, vaultAccount, vaultAccountData.currentEpoch.toNumber());
    const withdrawalReceipt = yield fetchWithdrawalReceipt(withdrawalReceiptAccount, connection);
    const tokenSupply = yield connection.getTokenSupply(new PublicKey(vaultInfo.accounts.vaultOwnershipTokenMint));
    const pendingWithdrawalInVaultTokens = withdrawalReceipt
        ? (withdrawalReceipt === null || withdrawalReceipt === void 0 ? void 0 : withdrawalReceipt.withdrawalAmount.toNumber()) /
            Math.pow(10, tokenSupply.value.decimals)
        : 0;
    if (stakePool) {
        // Try to fetch user's StakingRecord if StakePool is provided.
        const stakingRecord = yield getUserStakePosition(psyStakeProgramKey, walletAddress, stakePool, connection);
        if (stakingRecord) {
            if (stakingRecord.stakedAmount &&
                stakingRecord.stakedAmount.toNumber() > 0 &&
                stakingRecord.rewardUnits) {
                rewardMultiplier = round(Number(stakingRecord === null || stakingRecord === void 0 ? void 0 : stakingRecord.rewardUnits) /
                    Number(stakingRecord === null || stakingRecord === void 0 ? void 0 : stakingRecord.stakedAmount), 2, 2);
            }
            remainingLockupInSeconds = stakingRecord.remainingLockupInSeconds;
            vaultTokensStaked = stakingRecord.stakedAmount.toNumber();
            const stakePoolData = yield fetchStakePoolAccount(stakePool, connection);
            for (const poolReward of VaultPoolReward) {
                const rewardPool = yield fetchRewardPoolAccount(new PublicKey(poolReward.rewardPoolKey), connection);
                if (rewardPool) {
                    yield fetchRewardClaims(poolReward.rewardPoolKey, poolReward.rewardMintAddress, rewardPool.poolId, rewardPool.epochRewardDecimals, stakingRecord, stakePoolData, claimableRewards, psyStakeProgramKey, connection);
                }
            }
        }
    }
    const pendingCollateralAssetDeposits = ((_b = depositReceipt === null || depositReceipt === void 0 ? void 0 : depositReceipt.depositAmount) === null || _b === void 0 ? void 0 : _b.toNumber()) || 0;
    const valuePerVaultToken = vaultInfo.valuePerVaultToken;
    return {
        totalUserVaultTokens: vaultTokensInWallet + vaultTokensStaked,
        valuePerVaultToken,
        rewardMultiplier,
        pendingWithdrawalInVaultTokens,
        claimableRewards,
        vaultTokensInWallet,
        vaultTokensStaked,
        remainingLockupInSeconds,
        vaultTokenDecimals: userVaultTokenAmount === null || userVaultTokenAmount === void 0 ? void 0 : userVaultTokenAmount.decimals,
        pendingCollateralAssetDeposits,
    };
});
const getUserStakePosition = (psyStakeProgramKey, walletAddress, stakePool, connection) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        if (!stakePool)
            return;
        const [stakingRecordKey] = yield deriveStakingRecord(psyStakeProgramKey, walletAddress, stakePool);
        const stakingRecord = yield fetchStakingRecord(stakingRecordKey, connection);
        if (stakingRecord)
            return Object.assign(Object.assign({}, stakingRecord), { remainingLockupInSeconds: getRemainingTimeInSeconds(stakingRecord.lockUpExpiry.toNumber()) });
    }
    catch (e) {
        throw Error(e.message);
    }
});

const getAllUserVaultPositions = (userWallet, connection, isMainnet = true) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${BASE_URL}vaults${isMainnet ? "" : "?network=devnet"}`;
    const vaultResponse = yield axios.get(url);
    if (!vaultResponse) {
        throw Error("Unable to retrieve vaults from the api");
    }
    let vaults = {};
    yield Promise.all(Object.values(vaultResponse.data.vaults).map((vaultInfo) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const stakePoolKey = (_a = vaultInfo.staking) === null || _a === void 0 ? void 0 : _a.stakePoolKey;
        const VaultPoolReward = (_b = vaultInfo.staking) === null || _b === void 0 ? void 0 : _b.poolRewards;
        const result = yield getUserWalletPosition(userWallet, vaultInfo, new PublicKey(vaultInfo.accounts.vaultAddress), connection, stakePoolKey ? new PublicKey(stakePoolKey) : null, VaultPoolReward, isMainnet);
        if (!result)
            return;
        vaults[vaultInfo.id] = result;
    })));
    return vaults;
});
const getUserVaultPosition = (userWallet, vaultName, connection, isMainnet = true) => __awaiter(void 0, void 0, void 0, function* () {
    var _c, _d;
    const url = `${BASE_URL}vaults?id=${vaultName}${isMainnet ? "" : "&network=devnet"}`;
    const vaultResponse = yield axios.get(url);
    if (!vaultResponse.data) {
        throw Error("Unable to retrieve vault from the api");
    }
    const vaultInfo = vaultResponse.data["vaults"][vaultName];
    const stakePoolKey = (_c = vaultInfo.staking) === null || _c === void 0 ? void 0 : _c.stakePoolKey;
    const VaultPoolReward = (_d = vaultInfo.staking) === null || _d === void 0 ? void 0 : _d.poolRewards;
    const result = yield getUserWalletPosition(userWallet, vaultInfo, new PublicKey(vaultInfo.accounts.vaultAddress), connection, stakePoolKey ? new PublicKey(stakePoolKey) : null, VaultPoolReward, isMainnet);
    if (!result)
        return;
    return result;
});
function getVaultsInfo(isMainnet = true) {
    return __awaiter(this, void 0, void 0, function* () {
        const url = `${BASE_URL}vaults${isMainnet ? "" : "?network=devnet"}`;
        const vaultResponse = yield axios.get(url);
        if (!vaultResponse) {
            throw Error("Unable to retrieve vaults from the api");
        }
        const stats = vaultResponse.data.stats;
        const vault = vaultResponse.data.vaults;
        const lastModified = vaultResponse.data.lastModified;
        return {
            vault,
            stats,
            lastModified,
        };
    });
}

export { getAllUserVaultPositions, getUserStakePosition, getUserVaultPosition, getVaultsInfo, pdas };
